import React, { useEffect, useState } from 'react';

import { JabraCheckbox } from '@gn-audio/jss-components';

import { NavigationButtons } from '../components/NavigationButtons';
import { StepContainer } from '../components/StepContainer';
import { useHearingTest, useHearingTestTracking } from '../HearingTestContext';
import { translations } from '../translations/en';
import { DIFFICULTY_SITUATIONS, STEP_NAMES, TOTAL_STEPS } from '../types/HearingTestTypes';
import { HearingTestEventAction } from '../types/TrackingTypes';

const SITUATION_LABELS = {
  socialgatherings: translations.socialgatherings,
  restaurants: translations.restaurants,
  noisyenvironments: translations.noisyenvironments,
  meetingswork: translations.meetingswork,
  television: translations.television,
  onphone: translations.onphone,
  idontknow: translations.idontknow,
} as const;

export const LifestyleQuestions1Step: React.FC = () => {
  const { state, updateProfile, goToStep } = useHearingTest();
  const { trackHearingTestEvent } = useHearingTestTracking();
  const [selectedSituations, setSelectedSituations] = useState<string[]>(
    state.profile.difficultySituations || []
  );

  useEffect(() => {
    trackHearingTestEvent(HearingTestEventAction.LoadPage, 'Hearing Environment');
  }, [trackHearingTestEvent]);

  const handleNext = () => {
    const environmentString = selectedSituations.join(',');
    trackHearingTestEvent(HearingTestEventAction.SelectHearingEnvironment, environmentString);

    updateProfile({
      difficultySituations: selectedSituations,
    });
    goToStep(STEP_NAMES.LIFESTYLE_QUESTIONS_2);
  };

  const toggleSituation = (situation: string) => {
    setSelectedSituations((prev) => {
      if (prev.includes(situation)) {
        return prev.filter((s) => s !== situation);
      } else {
        return [...prev, situation];
      }
    });
  };

  const canProceed = selectedSituations.length > 0;
  const environmentsError = canProceed ? null : translations.environmentshelp;

  return (
    <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
      <div className="space-y-8">
        <div>
          <p className="mb-6 text-xl font-semibold text-gray-900">
            {translations.whenproblemshearing}
          </p>

          <div className="space-y-2">
            {DIFFICULTY_SITUATIONS.map((situation) => (
              <label key={situation} className="flex cursor-pointer items-center gap-3 py-1">
                <JabraCheckbox
                  checked={selectedSituations.includes(situation)}
                  onCheckedChange={() => toggleSituation(situation)}
                  className="mr-2 shrink-0 grow-0 border-gray-700 bg-white transition-colors duration-150 hover:border-accent focus:border-accent"
                />
                <span className="font-medium">{SITUATION_LABELS[situation]}</span>
              </label>
            ))}
          </div>
          {environmentsError && <p className="mt-2 text-sm text-error-600">{environmentsError}</p>}
        </div>

        <NavigationButtons onNext={handleNext} disableNext={!canProceed} />
      </div>
    </StepContainer>
  );
};

export default LifestyleQuestions1Step;
